import * as Sentry from '@sentry/nextjs';
import { startAuthentication } from '@simplewebauthn/browser';
import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/typescript-types';
import { Callback } from 'amplitude-js';
import { signIn, signOut } from 'next-auth/react';
import { Route } from 'utility/routes';
import { AnalyticsEvent } from '../analytics/events';
import { NativeAppMessage, sendNativeAppMessage } from '../nativeApp';

export const login = async (
  logEvent: <T extends string>(
    eventType: T,
    eventPropertiesIn?: object,
    callback?: Callback | undefined,
  ) => void,
  includeSSN: boolean,
) => {
  let scopes = process.env.NEXT_PUBLIC_MITID_SCOPES;

  if (includeSSN) {
    scopes = scopes + ' ssn';
  }
  logEvent(AnalyticsEvent.LOGIN);
  return await signIn('mitid', {}, { scope: scopes });
};

export const logout = async (
  logEvent: <T extends string>(
    eventType: T,
    eventPropertiesIn?: object,
    callback?: Callback | undefined,
  ) => void,
  logoutUrl: string,
) => {
  const agentNo = document.cookie
    .split('; ')
    .find((row) => row.startsWith('agentNo='))
    ?.split('=')[1];

  const agentCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('agent='));

  if (agentCookie) {
    document.cookie = 'agent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  if (agentNo) {
    document.cookie =
      'agentNo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  logEvent(AnalyticsEvent.LOGOUT);
  sendNativeAppMessage(NativeAppMessage.LOGGED_OUT, logoutUrl);
  Sentry.configureScope((scope) => scope.setUser(null));

  return signOut({
    callbackUrl: logoutUrl,
    redirect: true,
  });
};

export const signInWithWebauthn = async (
  localSessionId: string,
  bypassSignIn?: boolean,
) => {
  const url = new URL(Route.WEBAUTH_AUTHENTICATE, window.location.origin);
  url.searchParams.set('sessionId', localSessionId);
  const optionsResponse = await fetch(url.toString());

  if (optionsResponse.status !== 200) {
    throw new Error('Could not get authentication options from server');
  }
  const opt: PublicKeyCredentialRequestOptionsJSON =
    await optionsResponse.json();

  if (!opt.allowCredentials || opt.allowCredentials.length === 0) {
    throw new Error('There is no registered credential.');
  }

  const credential = await startAuthentication({ ...opt });

  if (bypassSignIn) {
    return opt;
  }

  await signIn('credentials', {
    id: credential.id,
    rawId: credential.rawId,
    type: credential.type,
    clientDataJSON: credential.response.clientDataJSON,
    authenticatorData: credential.response.authenticatorData,
    signature: credential.response.signature,
    userHandle: credential.response.userHandle,
    sessionId: localSessionId,
  });
};
